import type { LastLocation } from '@/lib/api/preferences.js';

/** A stored location older than this is forgotten: a week away is a fresh start, not a resume. */
export const LAST_LOCATION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Longer addresses are not worth keeping and would only bloat the preferences row. */
const MAX_HREF_LENGTH = 2_048;

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A page the app may reopen, and which of its query parameters describe the page itself. */
interface RestorableRoute {
  pattern: RegExp;
  searchKeys: readonly string[];
  /** A parameter the page cannot open without, with the shape it must have. */
  requires?: { key: string; shape: RegExp };
}

/** One path segment of an id route: letters, digits, `-` and `_`, never a slash or a dot. */
const SEGMENT = '[A-Za-z0-9_-]{1,80}';

const ARTIFACT_SEARCH_KEYS = [
  'tab',
  'file',
  'sp_conn',
  'sp_site',
  'sp_drive',
  'sp_folder',
  'sp_item',
  'od_scope',
  'od_folder',
  'od_item',
] as const;

/**
 * The pages worth reopening, and nothing else. Share and public views, sign-in screens, the admin
 * panel, dev tools and legacy redirects are left out on purpose, as is every one-shot parameter (a
 * prompt prefill, a "create workspace" or "connect" action, a run to prefill from): reopening those
 * would repeat an action instead of returning to a page.
 */
const RESTORABLE_ROUTES: readonly RestorableRoute[] = [
  { pattern: /^\/home\/?$/, searchKeys: [] },
  {
    pattern: /^\/chats\/?$/,
    searchKeys: ['conversationId'],
    requires: { key: 'conversationId', shape: UUID_RE },
  },
  { pattern: /^\/artifacts\/?$/, searchKeys: ARTIFACT_SEARCH_KEYS },
  {
    pattern: /^\/dashboards\/code-quality\/?$/,
    searchKeys: ['portfolio', 'account'],
  },
  { pattern: new RegExp(`^/dashboards(/${SEGMENT})?/?$`), searchKeys: [] },
  { pattern: new RegExp(`^/decks(/${SEGMENT})?/?$`), searchKeys: [] },
  { pattern: new RegExp(`^/diagrams(/${SEGMENT})?/?$`), searchKeys: [] },
  { pattern: /^\/developer(\/activity|\/spending)?\/?$/, searchKeys: [] },
  { pattern: new RegExp(`^/reports(/${SEGMENT})?/?$`), searchKeys: [] },
  { pattern: new RegExp(`^/scheduled(/${SEGMENT})?/?$`), searchKeys: [] },
  {
    pattern: /^\/memory\/?$/,
    searchKeys: ['tab', 'scope', 'view', 'block'],
  },
  { pattern: /^\/quality\/?$/, searchKeys: ['tab', 'projectId'] },
  { pattern: new RegExp(`^/quality/suites/${SEGMENT}/?$`), searchKeys: [] },
  { pattern: new RegExp(`^/quality/${SEGMENT}/?$`), searchKeys: [] },
  {
    pattern: new RegExp(`^/workspaces/${SEGMENT}/manage/?$`),
    searchKeys: ['tab'],
  },
  {
    pattern: new RegExp(`^/workspaces/${SEGMENT}/code-quality/${SEGMENT}/?$`),
    searchKeys: ['run', 'from'],
  },
  { pattern: /^\/code-quality\/?$/, searchKeys: [] },
  { pattern: /^\/contributors\/?$/, searchKeys: ['project'] },
  {
    pattern: /^\/tasks\/?$/,
    searchKeys: [
      'view',
      'task',
      'source',
      'priority',
      'author',
      'q',
      'direction',
      'status',
      'completed',
    ],
  },
];

/** Every route that never reopens, whatever the patterns above would say. */
const NEVER_RESTORED = [/\/shared?\//, /^\/quality\/(findings|suites)\/?$/];

/**
 * The address to remember for a page, or null when the page must never be reopened. Only the query
 * parameters that describe the page itself are kept.
 */
export function restorableHref(
  pathname: string,
  searchStr: string,
): string | null {
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return null;
  if (NEVER_RESTORED.some((pattern) => pattern.test(pathname))) return null;
  const route = RESTORABLE_ROUTES.find((candidate) =>
    candidate.pattern.test(pathname),
  );
  if (route === undefined) return null;
  const search = keptSearch(route, new URLSearchParams(searchStr));
  if (search === null) return null;
  const query = search.toString();
  const href = query.length > 0 ? `${pathname}?${query}` : pathname;
  return href.length <= MAX_HREF_LENGTH ? href : null;
}

/** The route's own parameters from `params`, or null when one it requires is missing or malformed. */
function keptSearch(
  route: RestorableRoute,
  params: URLSearchParams,
): URLSearchParams | null {
  const required = route.requires;
  if (required && !required.shape.test(params.get(required.key) ?? '')) {
    return null;
  }
  const kept = new URLSearchParams();
  for (const [key, value] of params) {
    if (route.searchKeys.includes(key)) kept.append(key, value);
  }
  return kept;
}

/**
 * Reads the `last_location` preference, checking it again rather than trusting what was stored: it
 * must be recent, well formed and an address inside this app that may be reopened. Anything else,
 * including a link that would leave the app, is treated as no location at all.
 */
export function parseLastLocation(
  raw: unknown,
  now = Date.now(),
): LastLocation | null {
  if (raw === null || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  if (typeof stored.href !== 'string' || typeof stored.at !== 'string') {
    return null;
  }
  const savedAt = Date.parse(stored.at);
  if (Number.isNaN(savedAt) || now - savedAt > LAST_LOCATION_MAX_AGE_MS) {
    return null;
  }
  const href = sanitizedHref(stored.href);
  if (href === null) return null;
  const workspaceId =
    typeof stored.workspace_id === 'string' ? stored.workspace_id : null;
  return { href, workspaceId, at: stored.at };
}

/** The stored address as a reopenable in-app address, or null when it points anywhere else. */
function sanitizedHref(href: string): string | null {
  if (href.length > MAX_HREF_LENGTH || !href.startsWith('/')) return null;
  const base = 'https://app.invalid';
  let url: URL;
  try {
    url = new URL(href, base);
  } catch {
    return null;
  }
  if (url.origin !== base) return null;
  return restorableHref(url.pathname, url.search);
}
